import { GameState } from '../types/game';
import { logger } from '../utils/logger';
import { scoreKeptDiceWithOrigins } from './keptDiceScoring';
import { makeEmptyDiceMask } from './gameRules';

export interface CaptureResult {
  segments: NonNullable<GameState['segments']>;
  capturedMask: boolean[];
}

// Helper: extracts kept dice from previous roll (based on current turnCycle state BEFORE applying the new roll)
// and appends a scoring segment if applicable. Non-mutating – returns updated segments and captured mask.
/**
 * Captures kept dice from the previous roll as a segment when applicable.
 */
export function capturePreviousKeptSegment(state: GameState): CaptureResult {
  const tc = state.turnCycle;
  const existingSegments = (state.segments ?? []) as NonNullable<GameState['segments']>;
  const ownerChanged = tc && state.segmentsOwnerId && tc.activePlayerId && state.segmentsOwnerId !== tc.activePlayerId;
  if (ownerChanged) {
    return { segments: [], capturedMask: makeEmptyDiceMask() };
  }
  if (!tc) return { segments: existingSegments, capturedMask: makeEmptyDiceMask() };
  if (tc.rollCount === 0) return { segments: existingSegments, capturedMask: tc.capturedMask || makeEmptyDiceMask() };
  if (existingSegments.length >= tc.rollCount) {
    return { segments: existingSegments, capturedMask: tc.capturedMask || makeEmptyDiceMask() };
  }

  const prevDice = tc.dice || [];
  const prevKept = tc.kept || makeEmptyDiceMask();
  const prevCaptured = tc.capturedMask || makeEmptyDiceMask();
  const lastRollIndex = typeof tc.lastRoll?.rollIndex === 'number' ? tc.lastRoll?.rollIndex : (tc.rollCount > 0 ? tc.rollCount - 1 : undefined);

  const capturableMask = prevKept.map((flag, idx) => flag && !prevCaptured[idx]);
  const scored = scoreKeptDiceWithOrigins({
    dice: prevDice,
    keptMask: capturableMask,
    capturedMask: prevCaptured,
    diceOrigins: tc.diceOrigins,
    lastRollIndex,
  });
  const keptDice = scored.keptDice;

  logger.debug('CAPTURE_SEGMENT', {
    rollCount: tc.rollCount,
    prevDice,
    prevKept,
    prevCaptured,
    capturableMask,
    keptDice,
    lastRollIndex,
    existingSegments: existingSegments.length,
  });

  if (keptDice.length === 0) {
    // Brak nowych kości do utrwalenia – NIE wykonujemy już automatycznego hot-dice capture.
    // Zgodnie z nowymi zasadami gracz MUSI jawnie zaznaczyć (keep) kości zanim zostaną dodane jako segment.
    logger.debug('CAPTURE_SKIP_EMPTY');
    return { segments: existingSegments, capturedMask: prevCaptured };
  }

  const sc = scored.score;
  if (sc.scoringDiceCount === 0) {
    logger.debug('CAPTURE_SKIP_NON_SCORING');
    return { segments: existingSegments, capturedMask: prevCaptured };
  }

  const newSegments = [...existingSegments, { dice: keptDice, points: sc.basePoints, scoringDiceCount: sc.scoringDiceCount, breakdown: sc.breakdown, breakdownDetails: sc.breakdownDetails }];
  const newCaptured = prevCaptured.map((flag, idx) => flag || capturableMask[idx]);
  
  logger.debug('CAPTURE_SUCCESS', {
    dice: keptDice,
    points: sc.basePoints,
    scoringCount: sc.scoringDiceCount,
    newCaptured,
  });
  
  return { segments: newSegments, capturedMask: newCaptured };
}

// Helper: finalizes last kept dice at TURN_END if not already captured as a segment.
/**
 * Finalizes kept dice into a segment when ending the turn.
 */
export function finalizeTrailingSegment(state: GameState): NonNullable<GameState['segments']> {
  const tc = state.turnCycle;
  if (!tc) return (state.segments ?? []) as NonNullable<GameState['segments']>;
  if (state.segmentsOwnerId && tc.activePlayerId && state.segmentsOwnerId !== tc.activePlayerId) {
    return [];
  }
  const segments = (state.segments ?? []) as NonNullable<GameState['segments']>;
  const prevDice = tc.dice || [];
  const prevKept = tc.kept || makeEmptyDiceMask();
  const capturedMask = tc.capturedMask || makeEmptyDiceMask();
  const lastRollIndex = typeof tc.lastRoll?.rollIndex === 'number' ? tc.lastRoll?.rollIndex : (tc.rollCount > 0 ? tc.rollCount - 1 : undefined);
  const scored = scoreKeptDiceWithOrigins({
    dice: prevDice,
    keptMask: prevKept,
    capturedMask,
    diceOrigins: tc.diceOrigins,
    lastRollIndex,
  });
  const keptDice = scored.keptDice;
  if (!keptDice.length) return segments;
  const sc = scored.score;
  if (sc.scoringDiceCount === 0) return segments;
  return [...segments, { dice: keptDice, points: sc.basePoints, scoringDiceCount: sc.scoringDiceCount, breakdown: sc.breakdown, breakdownDetails: sc.breakdownDetails }];
}
